import createPersistedState from "vuex-persistedstate";

// Saat ve son aktivite gibi sık, önemsiz commit'lerin kalıcı yazmayı en fazla ne kadar erteleyeceği.
// Oturum zaman aşımı 600 sn olduğundan yeni açılan sekme en fazla bu kadar eski lastActivityTime okur.
export const NOISY_WRITE_DELAY_MS = 30000;

// Kalıcı yazmayı zamanlar; storage ve DOM'dan bağımsızdır (test edilebilir olsun diye ayrı).
// - Aynı JS görevindeki commit'ler tek yazmada birleşir (microtask). Böylece commit + window.open akışında
//   yeni sekme yazılmış state'i okur.
// - noisy commit'ler yazmayı en fazla noisyDelayMs erteler; arada normal bir commit gelirse onunla birlikte yazılır.
export function createWriteScheduler({ noisyMutations = [], noisyDelayMs = NOISY_WRITE_DELAY_MS, setTimer = setTimeout, clearTimer = clearTimeout } = {}) {
  const noisy = new Set(noisyMutations);
  let pendingWrite = null;
  let microtaskQueued = false;
  let noisyTimer = null;

  const flush = () => {
    microtaskQueued = false;
    if (noisyTimer) {
      clearTimer(noisyTimer);
      noisyTimer = null;
    }
    if (!pendingWrite) return;
    const write = pendingWrite;
    pendingWrite = null;
    write();
  };

  const schedule = (mutationType, write) => {
    pendingWrite = write;
    if (!noisy.has(mutationType)) {
      if (!microtaskQueued) {
        microtaskQueued = true;
        Promise.resolve().then(flush);
      }
    } else if (!microtaskQueued && !noisyTimer) {
      noisyTimer = setTimer(flush, noisyDelayMs);
    }
  };

  return { schedule, flush };
}

function readStoredState(key, storage) {
  const value = storage.getItem(key);
  try {
    return typeof value === "string" ? JSON.parse(value) : undefined;
  } catch (e) {
    return undefined;
  }
}

// vuex-persistedstate varsayılan olarak her commit'te tüm state'i JSON.stringify ile storage'a yazar.
// Gün boyu açık kalan sekmede bu yük (saniyede bir saat commit'i, fare hareketi, sekmeler arası yayın)
// sekmenin çökmesine katkı veriyordu. Bu sarmalayıcı yazmaları createWriteScheduler ile seyreltir.
// mutationFilter false dönerse commit bu storage'a yazma tetiklemez; omitPaths ("gise", "item.itemsMap" gibi)
// rehydrate ve yazmadan çıkarılır.
export default function createThrottledPersistedState({ noisyMutations, mutationFilter = () => true, omitPaths = [], ...options } = {}) {
  const scheduler = createWriteScheduler({ noisyMutations });

  // Sekme kapanırken, yenilenirken ya da arka plana geçerken bekleyen yazma kaybolmasın.
  window.addEventListener("pagehide", scheduler.flush);
  window.addEventListener("beforeunload", scheduler.flush);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") scheduler.flush();
  });

  // Yol boyunca yalnız sığ kopya alınır; canlı store state'i değiştirilmez.
  const omit = (state) => {
    if (!state || omitPaths.length === 0) return state;
    const result = { ...state };
    omitPaths.forEach((path) => {
      const keys = path.split(".");
      const lastKey = keys.pop();
      let target = result;
      for (const key of keys) {
        if (!target[key] || typeof target[key] !== "object") return;
        target[key] = { ...target[key] };
        target = target[key];
      }
      delete target[lastKey];
    });
    return result;
  };

  return createPersistedState({
    ...options,
    getState: (key, storage) => omit(readStoredState(key, storage)),
    // paths verilmişse kütüphanenin kendi reducer'ı yalnız o yolları yazar.
    reducer: options.reducer || (options.paths ? undefined : omit),
    subscriber: (store) => (handler) =>
      store.subscribe((mutation, state) => {
        if (!mutationFilter(mutation)) return;
        scheduler.schedule(mutation.type, () => handler(mutation, state));
      }),
    setState: (key, state, storage) => {
      // Kota aşımı commit'i çağıran koda hata olarak sızmasın.
      try {
        storage.setItem(key, JSON.stringify(state));
      } catch (e) {
        console.warn("Kalıcı state yazılamadı:", key, e && e.name);
      }
    },
  });
}
